import React from 'react';
import { useAppSelector } from '../../../../../../../../app/hooks';
import styles from './SuggestionsPanel.module.scss';
import { SuggestedQuestion } from '../../../../../../../../store/dashboard/askAica/actions';
import { Mixpanel } from '../../../../../../../../utils/mixpanel';
import { AICA_ASK_AICA_SUGGESTION_CLICKED } from '../../../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { useParams } from 'react-router-dom';

interface SuggestionsPanelProps {
    onSuggestionClick?: (suggestion: SuggestedQuestion) => void;
}

const SuggestionsPanel: React.FC<SuggestionsPanelProps> = ({ onSuggestionClick }) => {
    const params = useParams();
    const user = useAppSelector((state) => state.user.userData);
    const suggestedQuestions = useAppSelector((state) => state.dashboard.askAicaReducer.suggestedQuestions);
    const isLoading = useAppSelector((state) => state.dashboard.askAicaReducer.isLoading);
    const isLoadingChatHistory = useAppSelector((state) => state.dashboard.askAicaReducer.isLoadingChatHistory);
    const messages = useAppSelector((state) => state.dashboard.askAicaReducer.messages);
    const orgId = params.id ? params.id : user?.investorOrgId;

    // Use the global suggested questions directly - no complex logic needed
    const suggestions: SuggestedQuestion[] = suggestedQuestions;

    const handleSuggestionClick = (suggestion: SuggestedQuestion) => {
        // Guard against clicks while loading to prevent duplicate calls
        if (isLoading) {
            return;
        }

        // track mixpanel event for suggestion click (different from message sent)
        Mixpanel.track(AICA_ASK_AICA_SUGGESTION_CLICKED, {
            suggestion: suggestion.text,
            organizationId: orgId,
            userId: user?.userId,
            pnoId: user?.investorOrgId,
        });

        if (onSuggestionClick) {
            onSuggestionClick(suggestion);
        }
    };

    // Show loading shimmer when loading chat history and no messages yet
    const showLoadingShimmer = isLoadingChatHistory && messages.length === 0;

    const renderLoadingShimmer = () => (
        <div className={styles.LoadingShimmer}>
            {[1, 2].map((index) => (
                <div key={index} className={styles.ShimmerItem}>
                    <div className={styles.ShimmerContent}>
                        <div className={`${styles.ShimmerLine} ${index === 1 ? styles.medium : styles.long}`}></div>
                        <div className={`${styles.ShimmerLine} ${styles.short}`}></div>
                    </div>
                    <div className={styles.ShimmerArrow}></div>
                </div>
            ))}
        </div>
    );

    return (
        <div className={styles.SuggestionsPanel}>
            <div className={styles.Header}>
                <p className={styles.Subtitle}>Related suggestion to ask AICA</p>
            </div>

            <div className={styles.SuggestionsList}>
                {showLoadingShimmer
                    ? renderLoadingShimmer()
                    : suggestions.map((suggestion) => (
                          <div
                              key={suggestion.id}
                              className={`${styles.SuggestionItem} ${isLoading ? styles.Disabled : ''}`}
                              onClick={() => handleSuggestionClick(suggestion)}
                          >
                              <div className={styles.SuggestionContent}>
                                  <div className={styles.SuggestionText}>
                                      {suggestion.display || suggestion.text}
                                      {suggestion.hint && <span className={styles.SuggestionHint}>{suggestion.hint}</span>}
                                  </div>
                              </div>
                              <div className={styles.SuggestionArrow}>→</div>
                          </div>
                      ))}
            </div>
        </div>
    );
};

export default SuggestionsPanel;
